import { Preferences } from "@capacitor/preferences";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { getFirebaseSingleton } from "@lib/firebase";
import { onLoggedOut } from "@src/app/store/session";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { ONBOARDING_USER_ERROR } from "@src/appV2/Worker/api/constants";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import {
  ActionType,
  LocalStorage,
  identifyAgent,
  loginZendesk,
  setEnvVariables,
  showAgentProfileFailedToLoadError,
} from "@store/session";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { populateSessionAfterLogin } from "./populateSessionAfterLogin";

export const useFetchAndInitializeWorker = (
  isAuthenticatedInFirebase: boolean,
  isAuthorized: boolean
) => {
  const dispatch = useDispatch();
  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const {
    data: agent,
    error,
    isLoading,
    isFetching: isFetchingWorker,
    fetchStatus: fetchStatusWorker,
    status: statusWorker,
  } = useGetWorker(
    {
      enabled: isAuthenticatedInFirebase,
    },
    isDebuggingEnabled
  );

  useEffect(() => {
    if (!isDefined(error)) {
      return;
    }

    if (toError(error).message !== ONBOARDING_USER_ERROR) {
      // This controls AgentProfileErrorModal component, which is surprisingly only rendered in onboarding
      // We should add some more unique error screen for all type of views
      showAgentProfileFailedToLoadError(dispatch);
    }
  }, [dispatch, error]);

  useEffect(() => {
    logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_QUERY_DEBUGGING, {
      isFetchingWorker,
      isLoading,
      isAuthenticatedInFirebase,
      isAuthorized,
      fetchStatusWorker,
      workerId: agent?.userId,
      statusWorker,
    });
  }, [
    isFetchingWorker,
    isLoading,
    isAuthenticatedInFirebase,
    isAuthorized,
    fetchStatusWorker,
    agent,
    statusWorker,
  ]);

  useEffect(() => {
    (async () => {
      logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_START);

      const user = getFirebaseSingleton().currentUser;
      // we don't wanna run this hook if agent is still fetching because it might cause race conditions of user id being set before
      // agent profile (when getUserByEmail finishes before actual agent fetch)
      if (!user || isLoading) {
        logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_SKIPPED, {
          isAgentQueryEnabled: isAuthenticatedInFirebase,
          isAgentQueryLoading: isLoading,
          isAuthenticatedInFirebase,
          isAuthorized,
          hasUserInFirebase: Boolean(user),
          email: user?.email,
        });
        return;
      }

      try {
        if (agent) {
          logEvent(APP_V2_APP_EVENTS.GET_WORKER_SUCCESS);

          dispatch({
            type: ActionType.SHOW_FETCH_WORKER_FAILURE_ALERT,
            data: {
              isFetchAgentProfileFailureAlertEnabled: false,
            },
          });

          await Preferences.set({
            key: LocalStorage.AGENT,
            value: JSON.stringify(agent),
          });

          void identifyAgent(dispatch, agent);
        }

        if (!isAuthorized) {
          logEvent(
            APP_V2_APP_EVENTS.SESSION_POPULATED_AFTER_LOGIN_START_USE_FETCH_AND_INITIALIZE_WORKER
          );
          await populateSessionAfterLogin(dispatch);
          logEvent(
            APP_V2_APP_EVENTS.SESSION_POPULATED_AFTER_LOGIN_END_USE_FETCH_AND_INITIALIZE_WORKER
          );
        } else {
          logEvent(APP_V2_APP_EVENTS.SESSION_POPULATE_AFTER_LOGIN_SKIPPED);
        }

        await Promise.allSettled([loginZendesk(), setEnvVariables(dispatch)]);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_FAILURE, {
          error,
          metadata: {
            phone: user.phoneNumber,
            email: user.email,
            name: user.displayName,
            uid: user.uid,
          },
        });

        // Dispatch a login failure action to clear session and allow for login.
        // Requiring a new login is better than hanging and requiring a force restart.
        if (isDebuggingEnabled) {
          onLoggedOut(dispatch);
        }
      }
    })();
  }, [dispatch, agent, isAuthorized, isAuthenticatedInFirebase, isLoading, isDebuggingEnabled]);
};
